<template>
  <v-dialog :model-value="modelValue" max-width="650" persistent @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <div class="d-flex align-center gap-2">
          <v-icon color="primary">mdi-key</v-icon>
          <span class="text-h6">{{ title }}</span>
        </div>
        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <v-alert type="info" variant="tonal" class="mb-4" border="start">
          <strong>{{ credentialLabel }}</strong> {{ guideMessage }}
        </v-alert>

        <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4" />

        <!-- Credentials List -->
        <template v-if="mycoidCredentials.length > 0">
          <div class="text-subtitle-2 mb-2 text-primary">
            <v-icon size="16" class="mr-1">mdi-key</v-icon>
            {{ credentialLabel }} {{ tl('Credentials') }} ({{ mycoidCredentials.length }})
          </div>
          <div class="credential-list">
            <v-card
              v-for="credential in mycoidCredentials"
              :key="credential.id"
              :class="['credential-card mb-3 pa-3 cursor-pointer', selectedId === credential.id ? 'selected' : '']"
              variant="outlined"
              @click="$emit('select', credential)"
            >
              <div class="d-flex align-center gap-3">
                <v-radio
                  :model-value="selectedId === credential.id"
                  hide-details
                  class="ma-0 flex-grow-0"
                />
                <v-avatar color="primary" size="40">
                  <v-icon color="white" size="20">mdi-key</v-icon>
                </v-avatar>
                <div class="flex-grow-1">
                  <div class="font-weight-medium">
                    {{ credential.alias }}
                    <v-chip size="x-small" color="success" variant="flat" class="ml-2">{{ credentialLabel }}</v-chip>
                  </div>
                  <div class="text-caption text-grey">
                    <v-icon size="12" class="mr-1">mdi-web</v-icon>
                    {{ credential.siteUrl }}
                  </div>
                  <div class="text-caption text-grey">
                    <v-icon size="12" class="mr-1">mdi-account</v-icon>
                    {{ credential.username }}
                  </div>
                </div>
              </div>
            </v-card>
          </div>
        </template>

        <!-- Manage Credentials -->
        <div v-if="mycoidCredentials.length > 0" class="d-flex justify-end mt-2">
          <v-btn
            variant="text"
            color="primary"
            size="small"
            @click="$emit('manage')"
          >
            <v-icon start size="16">mdi-cog</v-icon>
            {{ t('entity.Manage Credentials') || 'Manage Credentials' }}
          </v-btn>
        </div>

        <!-- No Credentials -->
        <template v-else-if="!isLoading">
          <v-alert type="error" variant="tonal" class="text-center">
            <div class="d-flex flex-column align-center gap-3">
              <v-icon size="48" color="error">mdi-key-alert</v-icon>
              <div>
                <strong>{{ credentialLabel }}</strong> {{ tl('credential not found.') }}<br />
                {{ tl('Register Credential First') }}
              </div>
              <v-btn color="primary" variant="flat" size="small" @click="$emit('manage')">
                <v-icon start size="18">mdi-plus</v-icon>
                {{ tl('Register Credential') }}
              </v-btn>
            </div>
          </v-alert>
        </template>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn variant="outlined" @click="$emit('update:modelValue', false)">{{ tl('Cancel') }}</v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!selectedId"
          @click="$emit('confirm')"
        >
          <v-icon start>mdi-arrow-right</v-icon>
          {{ tl('Next') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import type { Credential } from '@/features/Entity_my/types/credential';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

interface Props {
  modelValue: boolean;
  mycoidCredentials: Credential[];
  selectedId: string | null;
  isLoading: boolean;
  title?: string;
  guideMessage?: string;
  credentialLabel?: string;
}

withDefaults(defineProps<Props>(), {
  title: 'Select SSM Credential',
  guideMessage: 'credential is required for E-Filing.',
  credentialLabel: 'MYCOID2016',
});

defineEmits<{
  'update:modelValue': [value: boolean];
  select: [credential: Credential];
  confirm: [];
  manage: [];
}>();
</script>

<style scoped>
.credential-list {
  max-height: 350px;
  overflow-y: auto;
}

.credential-card {
  transition: all 0.2s ease;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
}

.credential-card:hover {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.02);
}

.credential-card.selected {
  border-color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
